"use client";

import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { OurServicesSectionProps } from "@/Templates/SectionsProps";
import { Icon } from "@iconify/react";
import {
  cloneElement,
  FC,
  isValidElement,
  ReactElement,
  ReactNode,
} from "react";
import HomeEighteenServicesSectionGenerated, {
  HomeEighteenServicesSectionGeneratedProps,
} from "../Sections/_generated/HomeEighteenServicesSection.generated";
import HomeElevenServicesSectionGenerated, {
  HomeElevenServicesSectionGeneratedProps,
} from "../Sections/_generated/HomeElevenServicesSection.generated";
import HomeFiveServiceSectionGenerated, {
  HomeFiveServiceSectionGeneratedProps,
} from "../Sections/_generated/HomeFiveServiceSection.generated";
import HomeFourServiceSectiionGenerated, {
  HomeFourServiceSectiionGeneratedProps,
} from "../Sections/_generated/HomeFourServiceSectiion.generated";
import HomeFourteenServicesSectionGenerated, {
  HomeFourteenServicesSectionGeneratedProps,
} from "../Sections/_generated/HomeFourteenServicesSection.generated";
import HomeNineServiceSectionGenerated, {
  HomeNineServiceSectionGeneratedProps,
} from "../Sections/_generated/HomeNineServiceSection.generated";
import HomeSevenServiceSectionGenerated, {
  HomeSevenServiceSectionGeneratedProps,
} from "../Sections/_generated/HomeSevenServiceSection.generated";
import HomeSeventeenServicesSectionGenerated, {
  HomeSeventeenServicesSectionGeneratedProps,
} from "../Sections/_generated/HomeSeventeenServicesSection.generated";
import HomeSixServiceSectionGenerated, {
  HomeSixServiceSectionGeneratedProps,
} from "../Sections/_generated/HomeSixServiceSection.generated";
import HomeSixteenServicesSectionGenerated, {
  HomeSixteenServicesSectionGeneratedProps,
} from "../Sections/_generated/HomeSixteenServicesSection.generated";
import HomeTenServiceSectionGenerated, {
  HomeTenServiceSectionGeneratedProps,
} from "../Sections/_generated/HomeTenServiceSection.generated";
import HomeThreeServicesSectionGenerated, {
  HomeThreeServicesSectionGeneratedProps,
} from "../Sections/_generated/HomeThreeServicesSection.generated";
import HomeTwelveServiceSectionGenerated, {
  HomeTwelveServiceSectionGeneratedProps,
} from "../Sections/_generated/HomeTwelveServiceSection.generated";
import HomeTwoServiceSectionGenerated, {
  HomeTwoServiceSectionGeneratedProps,
} from "../Sections/_generated/HomeTwoServiceSection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type GeneratedOurServicesRenderer = (
  props: OurServicesSectionProps,
) => ReactElement;

const OurServicesAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings
    .SiteTemplateOurServicesAppearance;

function renderHtmlValue(value?: string | null): ReactNode {
  if (!value) {
    return undefined;
  }

  return <Html value={value} />;
}

function cloneNodeWithChildren(
  defaultNode: ReactNode,
  children: ReactNode,
): ReactNode {
  if (!isValidElement(defaultNode)) {
    return children;
  }

  return cloneElement(defaultNode, undefined, children);
}

function createNodeChildrenRenderer(value?: ReactNode) {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  return (defaultNode: ReactNode) => cloneNodeWithChildren(defaultNode, value);
}

function getSectionImageUrl(
  props: OurServicesSectionProps,
): string | undefined {
  return props.setting.Image?.Url ?? undefined;
}

function getSectionImageAlt(
  props: OurServicesSectionProps,
  fallback: string,
): string {
  return props.setting.Image?.AltText ?? fallback;
}

function isImageLikeValue(value?: string | null): boolean {
  if (!value) {
    return false;
  }

  return (
    value.startsWith("/") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  );
}

function getItemImageUrl(
  props: OurServicesSectionProps,
  index: number,
): string | undefined {
  return props.lists?.Default?.[index]?.Image?.Url ?? undefined;
}

function getItemIconSource(
  props: OurServicesSectionProps,
  index: number,
  fallback?: string,
): string | undefined {
  const icon = props.lists?.Default?.[index]?.Icon;
  if (isImageLikeValue(icon)) {
    return icon ?? undefined;
  }

  return getItemImageUrl(props, index) ?? fallback;
}

function getItemIconName(
  props: OurServicesSectionProps,
  index: number,
  fallback?: string,
): string | undefined {
  return props.lists?.Default?.[index]?.Icon || fallback;
}

function getButtonVisible(props: OurServicesSectionProps): boolean {
  return (
    props.setting.ButtonDisplayStatus.Key ===
    CenterOptions.Enum.Status.Active.key
  );
}

function getServiceItems(props: OurServicesSectionProps) {
  return props.lists?.Default ?? [];
}

function applyHomeFiveItems(
  generatedProps: HomeFiveServiceSectionGeneratedProps,
  props: OurServicesSectionProps,
): HomeFiveServiceSectionGeneratedProps {
  const items = getServiceItems(props);

  return {
    ...generatedProps,
    sectionDiv0Div0Div0DivText:
      props.setting.BadgeText ?? generatedProps.sectionDiv0Div0Div0DivText,
    sectionDiv0Div0H20H2Text:
      props.setting.Title ?? generatedProps.sectionDiv0Div0H20H2Text,
    sectionDiv0Div0H20H2Renderer: createNodeChildrenRenderer(
      props.setting.Title,
    ),

    sectionDiv0Div1Div0Div0Div0ImageWithBasePath0ImageSrc:
      getItemIconSource(
        props,
        0,
        generatedProps.sectionDiv0Div1Div0Div0Div0ImageWithBasePath0ImageSrc,
      ) ?? generatedProps.sectionDiv0Div1Div0Div0Div0ImageWithBasePath0ImageSrc,
    sectionDiv0Div1Div0Div0H30H3Text:
      items[0]?.Title ?? generatedProps.sectionDiv0Div1Div0Div0H30H3Text,
    sectionDiv0Div1Div0Div0P0PText:
      renderHtmlValue(items[0]?.Description) ??
      generatedProps.sectionDiv0Div1Div0Div0P0PText,
    sectionDiv0Div1Div0Div0Link0LinkHref:
      items[0]?.Link ?? generatedProps.sectionDiv0Div1Div0Div0Link0LinkHref,
    sectionDiv0Div1Div0ImageWithBasePath0ImageSrc:
      getItemImageUrl(props, 0) ??
      generatedProps.sectionDiv0Div1Div0ImageWithBasePath0ImageSrc,

    sectionDiv0Div1Div1Div0Div0ImageWithBasePath0ImageSrc:
      getItemIconSource(
        props,
        1,
        generatedProps.sectionDiv0Div1Div1Div0Div0ImageWithBasePath0ImageSrc,
      ) ?? generatedProps.sectionDiv0Div1Div1Div0Div0ImageWithBasePath0ImageSrc,
    sectionDiv0Div1Div1Div0H30H3Text:
      items[1]?.Title ?? generatedProps.sectionDiv0Div1Div1Div0H30H3Text,
    sectionDiv0Div1Div1Div0P0PText:
      renderHtmlValue(items[1]?.Description) ??
      generatedProps.sectionDiv0Div1Div1Div0P0PText,
    sectionDiv0Div1Div1Div0Link0LinkHref:
      items[1]?.Link ?? generatedProps.sectionDiv0Div1Div1Div0Link0LinkHref,
    sectionDiv0Div1Div1ImageWithBasePath0ImageSrc:
      getItemImageUrl(props, 1) ??
      generatedProps.sectionDiv0Div1Div1ImageWithBasePath0ImageSrc,

    sectionDiv0Div1Div2Div0Div0ImageWithBasePath0ImageSrc:
      getItemIconSource(
        props,
        2,
        generatedProps.sectionDiv0Div1Div2Div0Div0ImageWithBasePath0ImageSrc,
      ) ?? generatedProps.sectionDiv0Div1Div2Div0Div0ImageWithBasePath0ImageSrc,
    sectionDiv0Div1Div2Div0H30H3Text:
      items[2]?.Title ?? generatedProps.sectionDiv0Div1Div2Div0H30H3Text,
    sectionDiv0Div1Div2Div0P0PText:
      renderHtmlValue(items[2]?.Description) ??
      generatedProps.sectionDiv0Div1Div2Div0P0PText,
    sectionDiv0Div1Div2Div0Link0LinkHref:
      items[2]?.Link ?? generatedProps.sectionDiv0Div1Div2Div0Link0LinkHref,
    sectionDiv0Div1Div2ImageWithBasePath0ImageSrc:
      getItemImageUrl(props, 2) ??
      generatedProps.sectionDiv0Div1Div2ImageWithBasePath0ImageSrc,

    sectionDiv0Div1Div3Div0Div0ImageWithBasePath0ImageSrc:
      getItemIconSource(
        props,
        3,
        generatedProps.sectionDiv0Div1Div3Div0Div0ImageWithBasePath0ImageSrc,
      ) ?? generatedProps.sectionDiv0Div1Div3Div0Div0ImageWithBasePath0ImageSrc,
    sectionDiv0Div1Div3Div0H30H3Text:
      items[3]?.Title ?? generatedProps.sectionDiv0Div1Div3Div0H30H3Text,
    sectionDiv0Div1Div3Div0P0PText:
      renderHtmlValue(items[3]?.Description) ??
      generatedProps.sectionDiv0Div1Div3Div0P0PText,
    sectionDiv0Div1Div3Div0Link0LinkHref:
      items[3]?.Link ?? generatedProps.sectionDiv0Div1Div3Div0Link0LinkHref,
    sectionDiv0Div1Div3ImageWithBasePath0ImageSrc:
      getItemImageUrl(props, 3) ??
      generatedProps.sectionDiv0Div1Div3ImageWithBasePath0ImageSrc,
  };
}

function applyHomeTwelveSection(
  generatedProps: HomeTwelveServiceSectionGeneratedProps,
  props: OurServicesSectionProps,
): HomeTwelveServiceSectionGeneratedProps {
  return {
    ...generatedProps,
    sectionDiv0Div0Div0H20H2Text:
      props.setting.Title ?? generatedProps.sectionDiv0Div0Div0H20H2Text,
    sectionDiv0Div0Div0H20Span0SpanText:
      props.setting.Subtitle ??
      generatedProps.sectionDiv0Div0Div0H20Span0SpanText,
    sectionDiv0Div0Div0H20H2Text2: "",
    sectionDiv0Div0Div0P0PText:
      renderHtmlValue(props.setting.Description) ??
      generatedProps.sectionDiv0Div0Div0P0PText,
    sectionDiv0Div0Link0LinkHref:
      props.setting.ButtonLink ?? generatedProps.sectionDiv0Div0Link0LinkHref,
    sectionDiv0Div0Link0Span1SpanText:
      props.setting.ButtonText ??
      generatedProps.sectionDiv0Div0Link0Span1SpanText,
    sectionDiv0Div0Link0LinkVisible: getButtonVisible(props),
  };
}

function applyHomeSixteenSection(
  generatedProps: HomeSixteenServicesSectionGeneratedProps,
  props: OurServicesSectionProps,
): HomeSixteenServicesSectionGeneratedProps {
  const items = getServiceItems(props);

  return {
    ...generatedProps,
    sectionDiv0Div0Div0DivText:
      props.setting.BadgeText ?? generatedProps.sectionDiv0Div0Div0DivText,
    sectionDiv0Div0H20H2Text:
      props.setting.Title ?? generatedProps.sectionDiv0Div0H20H2Text,
    sectionDiv0Div0H20Span0SpanText:
      props.setting.Subtitle ?? generatedProps.sectionDiv0Div0H20Span0SpanText,
    servicesSectionServicesLinkSixteenActives:
      generatedProps.servicesSectionServicesLinkSixteenActives.map(
        (item, index) => ({
          ...item,
          key: items[index]?.Id ?? item.key,
          h30Text: items[index]?.Title ?? item.h30Text,
          p0Text: renderHtmlValue(items[index]?.Description) ?? item.p0Text,
        }),
      ),
    servicesSectionTabPaneFadeShowActives:
      generatedProps.servicesSectionTabPaneFadeShowActives.map(
        (item, index) => ({
          ...item,
          key: items[index]?.Id ?? item.key,
          src: getItemImageUrl(props, index) ?? item.src,
          href: items[index]?.Link ?? item.href,
        }),
      ),
  };
}

function applyHomeSeventeenSection(
  generatedProps: HomeSeventeenServicesSectionGeneratedProps,
  props: OurServicesSectionProps,
): HomeSeventeenServicesSectionGeneratedProps {
  const items = getServiceItems(props);

  return {
    ...generatedProps,
    sectionDiv0Div0H20H2Text:
      props.setting.Title ?? generatedProps.sectionDiv0Div0H20H2Text,
    sectionDiv0Div0P0PText:
      renderHtmlValue(props.setting.Description) ??
      generatedProps.sectionDiv0Div0P0PText,
    sectionDiv2Link0LinkHref:
      props.setting.ButtonLink ?? generatedProps.sectionDiv2Link0LinkHref,
    sectionDiv2Link0Span0SpanText:
      props.setting.ButtonText ?? generatedProps.sectionDiv2Link0Span0SpanText,
    sectionDiv2Link0LinkVisible: getButtonVisible(props),
    servicesSectionSwipers: generatedProps.servicesSectionSwipers.map(
      (item, index) => ({
        ...item,
        key: items[index]?.Id ?? item.key,
        h30Text: items[index]?.Title ?? item.h30Text,
        p0Text: renderHtmlValue(items[index]?.Description) ?? item.p0Text,
        src: getItemImageUrl(props, index) ?? item.src,
      }),
    ),
  };
}

function applyHomeEighteenSection(
  generatedProps: HomeEighteenServicesSectionGeneratedProps,
  props: OurServicesSectionProps,
): HomeEighteenServicesSectionGeneratedProps {
  const items = getServiceItems(props);

  return {
    ...generatedProps,
    sectionDiv0Div0Div0DivText:
      props.setting.BadgeText ?? generatedProps.sectionDiv0Div0Div0DivText,
    sectionDiv0Div0H20H2Text:
      props.setting.Title ?? generatedProps.sectionDiv0Div0H20H2Text,
    sectionDiv0Div0H20H2Text2:
      props.setting.Subtitle ?? generatedProps.sectionDiv0Div0H20H2Text2,
    sectionDiv0Div0H20Span0ImageWithBasePath0ImageSrc:
      getSectionImageUrl(props) ??
      generatedProps.sectionDiv0Div0H20Span0ImageWithBasePath0ImageSrc,
    sectionDiv0Div0H20Span0ImageWithBasePath0ImageAlt: getSectionImageAlt(
      props,
      generatedProps.sectionDiv0Div0H20Span0ImageWithBasePath0ImageAlt ??
        "title",
    ),
    servicesSection3366s: generatedProps.servicesSection3366s.map(
      (item, index) => ({
        ...item,
        key: items[index]?.Id ?? item.key,
        src: getItemIconSource(props, index, item.src) ?? item.src,
        h30Text: items[index]?.Title ?? item.h30Text,
        p0Text: renderHtmlValue(items[index]?.Description) ?? item.p0Text,
        href: items[index]?.Link ?? item.href,
        imageWithBasePath0Src:
          getItemImageUrl(props, index) ?? item.imageWithBasePath0Src,
      }),
    ),
  };
}

const generatedOurServicesRenderers: Record<
  string,
  GeneratedOurServicesRenderer
> = {
  [OurServicesAppearance.Home2.key]: (props) => (
    <HomeTwoServiceSectionGenerated
      {...buildGeneratedSectionProps<HomeTwoServiceSectionGeneratedProps>(
        "HomeTwoServiceSection",
        {
          serviceSection246s: getServiceItems(props).map((item, index) => ({
            key: item.Id,
            className:
              index % 2 === 0
                ? "services-item-two bg-primary-transparent w-100 wow fadeInUp"
                : "services-item-two bg-success-transparent w-100 wow fadeInUp",
            dataWowDuration: `${Math.max(index + 1, 1)}s`,
            alt: item.Title ?? `service-${index + 1}`,
            src:
              getItemIconSource(props, index) ??
              item.Image?.Url ??
              "/Templates/Doccure/img/icons/booking.svg",
            link0Text: item.Title ?? "",
            href: item.Link ?? "#",
          })),
        },
      )}
    />
  ),
  [OurServicesAppearance.Home3.key]: (props) => (
    <HomeThreeServicesSectionGenerated
      {...buildGeneratedSectionProps<HomeThreeServicesSectionGeneratedProps>(
        "HomeThreeServicesSection",
        {
          servicesSectionServicesSlides: getServiceItems(props).map((item) => ({
            key: item.Id,
            h20Text: item.Title ?? "",
          })),
        },
      )}
    />
  ),
  [OurServicesAppearance.Home4.key]: (props) => (
    <HomeFourServiceSectiionGenerated
      {...buildGeneratedSectionProps<HomeFourServiceSectiionGeneratedProps>(
        "HomeFourServiceSectiion",
        {
          sectionDiv0Div0Div0Div0Div0DivText:
            props.setting.BadgeText ?? undefined,
          sectionDiv0Div0Div0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div0Div0Div0H20Span0SpanText:
            props.setting.Subtitle ?? undefined,
          serviceSectiionServicesFours: getServiceItems(props).map(
            (item, index) => ({
              key: item.Id,
              src:
                getItemIconName(props, index) ??
                "/Templates/Doccure/img/icons/service-08.svg",
              h30Text: item.Title ?? "",
              p0Text: renderHtmlValue(item.Description),
              imageWithBasePath0Src:
                item.Image?.Url ??
                "/Templates/Doccure/img/service/service-01.jpg",
            }),
          ),
          serviceSectiionServicesFoursImageRenderer: (item, defaultNode) => {
            if (!isValidElement<{ src?: string; className?: string }>(defaultNode)) {
              return defaultNode;
            }

            if (defaultNode.props.src !== item.src) {
              return defaultNode;
            }

            return (
              <Icon
                icon={item.src}
                width={48}
                height={48}
                className={defaultNode.props.className}
              />
            );
          },
        },
      )}
    />
  ),
  [OurServicesAppearance.Home5.key]: (props) => (
    <HomeFiveServiceSectionGenerated
      {...applyHomeFiveItems(
        buildGeneratedSectionProps<HomeFiveServiceSectionGeneratedProps>(
          "HomeFiveServiceSection",
        ),
        props,
      )}
    />
  ),
  [OurServicesAppearance.Home6.key]: (props) => (
    <HomeSixServiceSectionGenerated
      {...buildGeneratedSectionProps<HomeSixServiceSectionGeneratedProps>(
        "HomeSixServiceSection",
        {
          sectionDiv0Div0Div0DivText: props.setting.BadgeText ?? undefined,
          sectionDiv0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div0H20Span0SpanText: props.setting.Subtitle ?? undefined,
          sectionDiv0Div0H20H2Text2: "",
          sectionImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionImageWithBasePath0ImageAlt: getSectionImageAlt(
            props,
            "service",
          ),
          serviceSection366s: getServiceItems(props).map((item, index) => ({
            key: item.Id,
            src:
              getItemIconSource(props, index) ??
              "/Templates/Doccure/img/icons/services-icon-1.svg",
            h30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
            imageWithBasePath0Src:
              item.Image?.Url ??
              "/Templates/Doccure/img/service/service-img-1.jpg",
          })),
        },
      )}
    />
  ),
  [OurServicesAppearance.Home7.key]: (props) => (
    <HomeSevenServiceSectionGenerated
      {...buildGeneratedSectionProps<HomeSevenServiceSectionGeneratedProps>(
        "HomeSevenServiceSection",
        {
          sectionDiv0Div0Div0Div0Div0DivText:
            props.setting.BadgeText ?? undefined,
          sectionDiv0Div0Div0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div0Div0Div0H20Span0SpanText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div0Div1Div0Link0LinkHref:
            props.setting.ButtonLink ?? undefined,
          sectionDiv0Div0Div0Div1Div0Link0LinkText:
            props.setting.ButtonText ?? undefined,
          sectionDiv0Div0Div0Div1Div0Link0LinkVisible: getButtonVisible(props),
          sectionImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          serviceSectionServicesSevens: getServiceItems(props).map((item) => ({
            key: item.Id,
            h30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
            src:
              item.Image?.Url ??
              "/Templates/Doccure/img/service/service-05.jpg",
          })),
        },
      )}
    />
  ),
  [OurServicesAppearance.Home9.key]: (props) => (
    <HomeNineServiceSectionGenerated
      {...buildGeneratedSectionProps<HomeNineServiceSectionGeneratedProps>(
        "HomeNineServiceSection",
        {
          sectionDiv0Div0Div0DivText: props.setting.BadgeText ?? undefined,
          sectionDiv0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div0H20Span0SpanText: props.setting.Subtitle ?? undefined,
          sectionDiv0Div0H20H2Text2: "",
          sectionDiv0Div0ImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionDiv0Div0ImageWithBasePath0ImageAlt: getSectionImageAlt(
            props,
            "service",
          ),
          serviceSectionServicesNines: getServiceItems(props).map(
            (item, index) => ({
              key: item.Id,
              src:
                getItemIconSource(props, index) ??
                "/Templates/Doccure/img/icons/services-icon-two.svg",
              h30Text: item.Title ?? "",
              h30H30Text: item.Title ?? "",
              p0Text: renderHtmlValue(item.Description),
              imageWithBasePath0Src:
                item.Image?.Url ??
                "/Templates/Doccure/img/service/services-img-one.jpg",
              span0Text: item.LinkText ?? undefined,
            }),
          ),
        },
      )}
    />
  ),
  [OurServicesAppearance.Home10.key]: (props) => (
    <HomeTenServiceSectionGenerated
      {...buildGeneratedSectionProps<HomeTenServiceSectionGeneratedProps>(
        "HomeTenServiceSection",
        {
          sectionDiv0Div0Div0Div0Div0Div0DivText:
            props.setting.BadgeText ?? undefined,
          sectionDiv0Div0Div0Div0Div0H20H2Text:
            props.setting.Title ?? undefined,
          sectionDiv0Div0Div0Div1Div0Link0LinkHref:
            props.setting.ButtonLink ?? undefined,
          sectionDiv0Div0Div0Div1Div0Link0LinkText:
            props.setting.ButtonText ?? undefined,
          sectionDiv0Div0Div0Div1Div0Link0LinkVisible: getButtonVisible(props),
          serviceSection46s: getServiceItems(props).map((item, index) => ({
            key: item.Id,
            dataWowDuration: `${Math.max(index + 1, 1)}s`,
            src:
              getItemIconSource(props, index) ??
              "/Templates/Doccure/img/icons/service-12.svg",
            imageWithBasePath0Src:
              item.Image?.Url ??
              "/Templates/Doccure/img/service/service-10.jpg",
            h30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
          })),
        },
      )}
    />
  ),
  [OurServicesAppearance.Home11.key]: (props) => (
    <HomeElevenServicesSectionGenerated
      {...buildGeneratedSectionProps<HomeElevenServicesSectionGeneratedProps>(
        "HomeElevenServicesSection",
        {
          sectionDiv0Div0Div0DivText: props.setting.BadgeText ?? undefined,
          sectionDiv0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div2Link0LinkHref: props.setting.ButtonLink ?? undefined,
          sectionDiv0Div2Link0LinkText: props.setting.ButtonText ?? undefined,
          sectionDiv0Div2Link0LinkVisible: getButtonVisible(props),
          sectionImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          servicesSectionAccordions: getServiceItems(props).map(
            (item, index) => ({
              key: item.Id,
              className:
                index === 0
                  ? "accordion-collapse collapse show"
                  : "accordion-collapse collapse",
              src:
                item.Image?.Url ?? "/Templates/Doccure/img/about/faq-img-1.jpg",
              id: `serviceCollapse${index + 1}`,
              h30Id: `serviceHeading${index + 1}`,
              link0Text: item.Title ?? "",
              ariaControls: `serviceCollapse${index + 1}`,
              ariaExpanded: index === 0,
              link0ClassName:
                index === 0 ? "accordion-button" : "accordion-button collapsed",
              dataBsTarget: `#serviceCollapse${index + 1}`,
              span0Text: renderHtmlValue(item.Description),
              dataBsParent: "#faq-details-one",
              dataBsToggle: "collapse",
              href: item.Link ?? "#",
            }),
          ),
        },
      )}
    />
  ),
  [OurServicesAppearance.Home12.key]: (props) => (
    <HomeTwelveServiceSectionGenerated
      {...applyHomeTwelveSection(
        buildGeneratedSectionProps<HomeTwelveServiceSectionGeneratedProps>(
          "HomeTwelveServiceSection",
        ),
        props,
      )}
    />
  ),
  [OurServicesAppearance.Home14.key]: (props) => (
    <HomeFourteenServicesSectionGenerated
      {...buildGeneratedSectionProps<HomeFourteenServicesSectionGeneratedProps>(
        "HomeFourteenServicesSection",
        {
          sectionDiv0Div0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div0Div0H20Span0SpanText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div0H20Span1SpanText:
            props.setting.Description ?? undefined,
          sectionDiv0Div0Link0LinkHref: props.setting.ButtonLink ?? undefined,
          sectionDiv0Div0Link0LinkText: props.setting.ButtonText ?? undefined,
          sectionDiv0Div0Link0LinkVisible: getButtonVisible(props),
          sectionImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          servicesSectionServicesFourteenFadeInUps: getServiceItems(props).map(
            (item, index) => ({
              key: item.Id,
              dataWowDuration: `${index + 1}s`,
              h30Text: item.Title ?? "",
              p0Text: renderHtmlValue(item.Description),
              src:
                getItemIconSource(props, index) ??
                item.Image?.Url ??
                "/Templates/Doccure/img/service/service-thumb-01.jpg",
              imageWithBasePath1Src:
                item.Image?.Url ??
                "/Templates/Doccure/img/service/service-lg-01.jpg",
              h30H30Text: item.Title ?? "",
              href: item.Link ?? "#",
            }),
          ),
        },
      )}
    />
  ),
  [OurServicesAppearance.Home16.key]: (props) => (
    <HomeSixteenServicesSectionGenerated
      {...applyHomeSixteenSection(
        buildGeneratedSectionProps<HomeSixteenServicesSectionGeneratedProps>(
          "HomeSixteenServicesSection",
        ),
        props,
      )}
    />
  ),
  [OurServicesAppearance.Home17.key]: (props) => (
    <HomeSeventeenServicesSectionGenerated
      {...applyHomeSeventeenSection(
        buildGeneratedSectionProps<HomeSeventeenServicesSectionGeneratedProps>(
          "HomeSeventeenServicesSection",
        ),
        props,
      )}
    />
  ),
  [OurServicesAppearance.Home18.key]: (props) => (
    <HomeEighteenServicesSectionGenerated
      {...applyHomeEighteenSection(
        buildGeneratedSectionProps<HomeEighteenServicesSectionGeneratedProps>(
          "HomeEighteenServicesSection",
        ),
        props,
      )}
    />
  ),
};

const GeneratedOurServicesSection: FC<OurServicesSectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedOurServicesRenderers,
    OurServicesAppearance.Home2.key,
    (x) => x.setting.Appearance?.Key,
  );
};

export default GeneratedOurServicesSection;
